import { AzureOpenAI } from 'openai';

// 延迟初始化Azure OpenAI客户端
let client: AzureOpenAI | null = null;

const getClient = (): AzureOpenAI => {
  if (!client) {
    // 验证必需的环境变量
    const apiKey = process.env.REACT_APP_AZURE_OPENAI_API_KEY;
    const endpoint = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT;
    
    if (!apiKey) {
      throw new Error('缺少 REACT_APP_AZURE_OPENAI_API_KEY 环境变量');
    }
    
    if (!endpoint) {
      throw new Error('缺少 REACT_APP_AZURE_OPENAI_ENDPOINT 环境变量');
    }
    
    client = new AzureOpenAI({
      apiKey,
      endpoint,
      apiVersion: process.env.REACT_APP_AZURE_OPENAI_API_VERSION || '2024-02-15-preview',
      deployment: process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT_NAME || 'gpt-35-turbo',
      dangerouslyAllowBrowser: true, // ⚠️ 仅用于开发环境，生产环境需要后端代理
    });
  }
  
  return client;
};

// 检查是否是拒绝回答的回复
const isRefusalResponse = (content: string): boolean => {
  const refusalPatterns = [
    '我无法回答',
    '超出了我的知识范围',
    '我没有相关信息',
    '不在我的数据范围内',
    '根据现有资料，我无法找到相关信息',
    'I cannot answer',
    'I don\'t have information',
    'outside my knowledge',
    'not in my data'
  ];
  
  const lowerContent = content.toLowerCase();
  return refusalPatterns.some(pattern => 
    lowerContent.includes(pattern.toLowerCase())
  );
};

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatCompletionOptions {
  messages: ChatMessage[];
  maxTokens?: number;
  temperature?: number;
  stream?: boolean;
}

// 发送聊天消息到Azure OpenAI (支持RAG和智能回退)
export const sendChatMessage = async (
  messages: ChatMessage[],
  options: Partial<ChatCompletionOptions> = {}
): Promise<string> => {
  try {
    const clientInstance = getClient();
    
    // 首先尝试使用RAG模式
    const ragEnabled = !!(
      process.env.REACT_APP_AZURE_SEARCH_ENDPOINT && 
      process.env.REACT_APP_AZURE_SEARCH_API_KEY && 
      process.env.REACT_APP_AZURE_SEARCH_INDEX_NAME
    );

    if (ragEnabled) {
      // 构建RAG请求参数
      const ragParams: any = {
        model: '', 
        messages: messages,
        max_tokens: options.maxTokens || 1000,
        temperature: options.temperature || 0.7,
        stream: false,
        data_sources: [{
          type: "azure_search",
          parameters: {
            endpoint: process.env.REACT_APP_AZURE_SEARCH_ENDPOINT,
            index_name: process.env.REACT_APP_AZURE_SEARCH_INDEX_NAME,
            authentication: {
              type: "api_key",
              key: process.env.REACT_APP_AZURE_SEARCH_API_KEY
            },
            query_type: "semantic",
            semantic_configuration: "default",
            top_n_documents: 5,
            strictness: 1, // 降低严格性
            in_scope: false // 允许超出数据源范围的回答
          }
        }]
      };

      const response = await clientInstance.chat.completions.create(ragParams);
      
      if (response.choices && response.choices.length > 0) {
        const content = response.choices[0].message?.content;
        
        // 检查是否是拒绝回答的回复
        if (content && !isRefusalResponse(content)) {
          return content;
        }
      }
    }

    // 如果RAG模式失败或返回拒绝回复，使用标准模式
    const standardParams: any = {
      model: '',
      messages: messages,
      max_tokens: options.maxTokens || 1000,
      temperature: options.temperature || 0.7,
      stream: false,
    };

    const response = await clientInstance.chat.completions.create(standardParams);

    if (response.choices && response.choices.length > 0) {
      return response.choices[0].message?.content || '抱歉，我无法生成回复。';
    } else {
      throw new Error('未收到有效回复');
    }
  } catch (error: any) {
    console.error('Azure OpenAI API 错误:', error);
    
    if (error.status) {
      // 新版SDK的错误格式
      const status = error.status;
      const message = error.message || error.error?.message || '未知错误';
      
      switch (status) {
        case 401:
          throw new Error('API密钥无效，请检查您的Azure OpenAI配置');
        case 429:
          throw new Error('请求过于频繁，请稍后再试');
        case 500:
          throw new Error('Azure OpenAI服务暂时不可用');
        default:
          throw new Error(`API错误 (${status}): ${message}`);
      }
    } else if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
      throw new Error('无法连接到Azure OpenAI服务，请检查网络连接');
    } else {
      throw new Error(`请求失败: ${error.message}`);
    }
  }
};

// 添加系统提示词，用于基于自有数据的回答
export const createSystemMessage = (context?: string): ChatMessage => {
  const basePrompt = `你是一个专业的AI助手。你拥有广泛的知识，同时可以访问特定的知识库来提供更准确的信息。

请遵循以下原则：
1. **优先使用知识库信息**：如果知识库中有相关信息，请优先基于这些信息回答
2. **结合通用知识**：当知识库信息不足时，可以结合你的通用知识来提供更完整的回答
3. **明确信息来源**：在回答时，请适当标明信息是来自知识库还是通用知识
4. **不要拒绝回答**：即使知识库中没有直接相关的信息，也要尽力基于你的通用知识来回答用户的问题
5. **保持有用性**：始终努力为用户提供有用、准确和相关的信息

回答格式建议：
- 如果知识库有相关信息："根据我的资料库..."
- 如果需要补充通用知识："基于我的了解，另外..."
- 如果主要依赖通用知识："虽然我的专业资料库中没有详细信息，但我可以为您提供以下信息..."`;
  
  const fullPrompt = context 
    ? `${basePrompt}\n\n专业资料库内容：\n${context}`
    : basePrompt;
    
  return {
    role: 'system',
    content: fullPrompt
  };
};

// 创建标准模式的系统提示词（不使用知识库时）
export const createStandardSystemMessage = (): ChatMessage => {
  return {
    role: 'system',
    content: `你是一个专业的AI助手。请基于你的广泛知识为用户提供有用、准确的信息和建议。

请遵循以下原则：
1. 提供准确、有用的信息
2. 承认知识的局限性
3. 保持专业和友好的语调
4. 根据问题的复杂性调整回答的详细程度
5. 在不确定时，明确表达不确定性`
  };
};

// 验证Azure OpenAI配置
export const validateConfiguration = (): boolean => {
  const requiredEnvVars = [
    'REACT_APP_AZURE_OPENAI_API_KEY',
    'REACT_APP_AZURE_OPENAI_ENDPOINT',
    'REACT_APP_AZURE_OPENAI_DEPLOYMENT_NAME'
  ];

  for (const envVar of requiredEnvVars) {
    if (!process.env[envVar]) {
      console.error(`缺少必需的环境变量: ${envVar}`);
      return false;
    }
  }

  return true;
}; 